<template>
  <div class="chart-container">
    <div class="chart-header">{{ chartHeader }}</div>
    <svg class="barchart" :width="svgWidth" :height="svgHeight">
      <g :transform="`translate(${marginLeft / 2}, ${marginTop / 2})`">
        <g
          class="x-axis"
          fill="none"
          :transform="`translate(0, ${height})`"
          style="`color: ${barChart.AXIS_COLOR}`"
        >
          <path
            class="domain"
            stroke="currentColor"
            :d="`M0.5,6V0.5H${width}.5V6`"
          ></path>
          <g
            :class="xAxis.class"
            :font-weight="xAxis.fontWeight"
            :font-family="xAxis.fontFamily"
            :text-anchor="xAxis.textAnchor"
            v-for="(bar, index) in bars"
            :key="index"
            :transform="`translate(${bar.x + bar.width / 2}, 0)`"
          >
            <line stroke="currentColor" y2="6"></line>
            <text
              fill="currentColor"
              class="chart-text"
              y="9"
              dy="0.71em"
              v-if="showXLabel(index)"
            >
              {{ formatedDate(bar.xLabel) }}
            </text>
          </g>
        </g>
        <g
          class="y-axis"
          fill="none"
          :transform="`translate(0, 0)`"
          style="`color: ${barChart.AXIS_COLOR}`"
        >
          <path
            class="domain"
            stroke="currentColor"
            :d="`M0.5,${height}.5H0.5V0.5H-6`"
          ></path>
          <g
            :class="yAxis.class"
            :font-family="yAxis.fontFamily"
            :text-anchor="yAxis.textAnchor"
            v-for="(tick, index) in yTicks"
            :key="index"
            :transform="`translate(0, ${y(tick) + 0.5})`"
          >
            <line stroke="currentColor" x2="-4"></line>
            <text fill="currentColor" x="-6" dy="0.32em" class="chart-text">
              {{ tick }}
            </text>
          </g>
        </g>
        <g class="bars" fill="none">
          <g
            v-for="(bar, index) in bars"
            :key="index"
            :class="renderType"
            class="chart-rect-container"
            :data-count="bars.length"
            :data-index="index"
            :data-color="barColor"
            :data-hover-color="barHoverColor"
            :data-current-color="barCurrentColor"
            :data-current-hover-color="barCurrentHoverColor"
          >
            <text
              fill="currentColor"
              :x="bar.x + bar.width / 2"
              :y="bar.y - 5"
              text-anchor="middle"
              :class="isDaily ? 'is-hidden' : ''"
              class="chart-text"
            >
              {{ bar.yLabel }}
            </text>
            <rect
              :fill="index === bars.length - 1 ? barCurrentColor : barColor"
              class="chart-rect"
              :height="bar.height"
              :width="bar.width"
              :x="bar.x"
              :y="bar.y"
            ></rect>
          </g>
        </g>
      </g>
    </svg>
    <div class="chart-footer">{{ chartFooter }}</div>
  </div>
</template>

<script>
import * as moment from 'moment';
import { scaleLinear, scaleBand } from 'd3-scale';
import { select, selectAll } from 'd3-selection';

import { DAILY, DATE_FORMAT1, DATE_FORMAT2, MONTHLY } from '../constants';

function _handleMouseEvent(type) {
  return function() {
    const parent = select(this);
    const length = parent.attr('data-count');
    const index = parent.attr('data-index');
    const currentHoverColor = parent.attr('data-current-hover-color');
    const hoverColor = parent.attr('data-hover-color');
    const currentColor = parent.attr('data-current-color');
    const color = parent.attr('data-color');

    let fillColor = length - 1 == index ? currentHoverColor : hoverColor;

    if (type === 'mouseout') {
      fillColor = length - 1 == index ? currentColor : color;
    }

    parent.select('.chart-rect').style('fill', fillColor);

    if (parent.classed(DAILY)) {
      parent
        .select('.chart-text')
        .attr('class', `chart-text ${type === 'mouseout' ? 'is-hidden' : ''}`);
    }
  };
}

export default {
  name: 'BarChart',
  props: {
    chartHeader: { default: '' },
    chartFooter: { default: '' },
    height: { default: 200 },
    width: { default: 500 },
    dataSet: { default: [] },
    dataFields: { default: [] },
    marginLeft: { default: 40 },
    marginTop: { default: 40 },
    marginBottom: { default: 40 },
    marginRight: { default: 40 },
    tickCount: { default: 5 },
    barPadding: { default: 0.3 },
    barColor: { default: '' },
    barHoverColor: { default: '' },
    barCurrentColor: { default: '' },
    barCurrentHoverColor: { default: '' },
    renderType: { default: '' },
  },
  data() {
    return {
      barChart: {
        AXIS_COLOR: '#888',
      },
      xAxis: {
        class: 'tick',
        fontWeight: 'bold',
        fontFamily: 'sans-serif',
        textAnchor: 'middle',
      },
      yAxis: {
        class: 'tick',
        fontFamily: 'sans-serif',
        textAnchor: 'end',
      },
    };
  },
  updated() {
    this.attachEventHandlers();
  },
  mounted() {
    this.attachEventHandlers();
  },
  computed: {
    yTicks() {
      return this.y.ticks(this.tickCount);
    },
    x() {
      return scaleBand()
        .range([0, this.width])
        .padding(this.barPadding)
        .domain(this.dataSet.map(e => e[this.dataFields[0]]));
    },
    y() {
      const values = this.dataSet.map(e => e[this.dataFields[1]]);
      return scaleLinear()
        .range([this.height, 0])
        .domain([0, Math.max(...values)]);
    },
    bars() {
      const bars = this.dataSet.map(d => {
        return {
          xLabel: d[this.dataFields[0]],
          yLabel: this.formatNumber(d[this.dataFields[1]]),
          x: this.x(d[this.dataFields[0]]),
          y: this.y(d[this.dataFields[1]]),
          width: this.x.bandwidth(),
          height: this.height - this.y(d[this.dataFields[1]]),
        };
      });

      return bars;
    },
    xTickGap() {
      return this.renderType === MONTHLY ? 2 : 20;
    },
    svgWidth() {
      return this.width + this.marginLeft / 2;
    },
    svgHeight() {
      return this.height + this.marginTop;
    },
    isDaily() {
      return this.renderType === DAILY;
    },
  },
  methods: {
    attachEventHandlers() {
      selectAll('.chart-rect-container')
        .on('mouseover', _handleMouseEvent('mouseover'))
        .on('mouseout', _handleMouseEvent('mouseout'));
    },
    formatedDate(date) {
      return moment(date).format(
        this.renderType === MONTHLY ? DATE_FORMAT1 : DATE_FORMAT2
      );
    },
    formatNumber(number) {
      return new Intl.NumberFormat().format(number);
    },
    showXLabel(index) {
      return index % this.xTickGap === 0;
    },
  },
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
.chart-container {
  background-color: $white;
  color: #151515;
  border-radius: $radius-extra-large;
  box-shadow: 0 0.125rem 0.25rem 0 rgba($black, 0.02),
    0 0.125rem 0.75rem 0 rgba($black, 0.06);
  padding: 0 0 0.625rem 0.75rem;

  .chart-footer {
    text-align: center;
    font-weight: $weight-bold;
  }

  .chart-header {
    padding: 0.625rem 0 0 1.25rem;
    font-weight: $weight-bold;
  }

  .chart-text {
    font-size: 0.6875rem;
    font-weight: bold;
  }

  .chart-rect:hover {
    cursor: pointer;
  }
}
</style>
